import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Monitor, Zap, Rocket, Trophy, CheckCircle, Loader } from "lucide-react";
import { IMAGES } from "../data/images";
import CTABand from "../components/shared/CTABand";
import { toast } from "sonner";
import api from "../utils/api";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const EXHIBITOR_CATEGORIES = [
  "AI Platforms & Tools", "Cloud & Infrastructure", "Enterprise AI Software",
  "Data & Analytics", "Cybersecurity", "Robotics & Automation",
  "Healthcare AI", "Financial Services AI", "AI Startups",
  "Developer Tools", "MLOps & Deployment", "Industry-Specific AI",
];

const BOOTHS = [
  {
    icon: Trophy,
    title: "Premium Booth",
    size: "20'×20' Prime Location",
    inquiryType: "premium_booth",
    perks: ["Corner or end-cap position", "Premium AV package included", "Priority in exhibitor directory", "Featured logo on event materials"],
    color: "#7B2FBE",
  },
  {
    icon: Monitor,
    title: "Standard Booth",
    size: "10'×10' Standard",
    inquiryType: "standard_booth",
    perks: ["Exhibitor directory listing", "Basic AV setup", "Table & chairs included", "Lead retrieval access"],
    color: "#00D4FF",
  },
  {
    icon: Rocket,
    title: "Startup Pod",
    size: "6'×6' Startup Zone",
    inquiryType: "startup_pod",
    perks: ["Startup pavilion location", "Pitch session opportunity", "Startup directory listing", "Networking event access"],
    color: "#00D4FF",
  },
];

/* ── Exhibitor Inquiry Form ────────────────────────── */
function ExhibitorInquiryForm({ initialType = "standard_booth" }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    inquiry_type: initialType, message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("Inquiry received — we'll follow up shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-10" data-testid="exhibitor-inquiry-success">
        <CheckCircle size={36} className="mx-auto mb-4" style={{ color: "var(--cyan)" }} />
        <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>
          We'll be in touch.
        </h3>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Our expo team will follow up within 2 business days with availability and full details.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="exhibitor-inquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Full Name *</label>
          <input name="name" value={form.name} onChange={handleChange} required placeholder="Jane Smith" className="input-dark" data-testid="exhibitor-name-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Work Email *</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="jane@company.com" className="input-dark" data-testid="exhibitor-email-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Company *</label>
          <input name="company" value={form.company} onChange={handleChange} required placeholder="Your Company" className="input-dark" data-testid="exhibitor-company-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Phone</label>
          <input name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" className="input-dark" data-testid="exhibitor-phone-input" />
        </div>
      </div>
      <div>
        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Booth Interest</label>
        <select name="inquiry_type" value={form.inquiry_type} onChange={handleChange} className="input-dark" data-testid="exhibitor-type-select">
          <option value="premium_booth">Premium Booth — 20'×20' Prime Location</option>
          <option value="standard_booth">Standard Booth — 10'×10'</option>
          <option value="startup_pod">Startup Pod — 6'×6' Startup Zone</option>
          <option value="general">Not sure yet — send me all options</option>
        </select>
      </div>
      <div>
        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Tell us about your company</label>
        <textarea name="message" value={form.message} onChange={handleChange} rows={3}
          placeholder="What products or services will you be showcasing?" className="input-dark resize-none" data-testid="exhibitor-message-input" />
      </div>
      <button type="submit" disabled={loading || !form.name || !form.email || !form.company}
        className="btn-primary" data-testid="exhibitor-submit-btn"
        style={{ opacity: loading || (!form.name || !form.email || !form.company) ? 0.65 : 1 }}>
        {loading ? <><Loader size={14} className="animate-spin" /> Sending...</> : <>Request Booth Information <ArrowRight size={14} /></>}
      </button>
      <p className="text-xs" style={{ color: "var(--text-muted)", opacity: 0.7 }}>
        No commitment required. We'll follow up with availability and full details.
      </p>
    </form>
  );
}

export default function ExpoPage() {
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="expo-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden pb-20" data-testid="expo-hero">
        <img src={IMAGES.expo} alt="Expo Hall" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(9,13,22,0.4) 0%, rgba(9,13,22,0.95) 100%)" }} />
        <div className="relative z-10 container-xl pt-40">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">Expo & Innovation</div>
            <h1 className="text-5xl sm:text-6xl font-black text-white mb-4"
              style={{ fontFamily: "var(--font-heading)" }}>Where solutions meet problems.</h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              60+ exhibitors. Live demos. The startup zone. The innovation showcase. Designed to help you find the tools and partners your business needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ backgroundColor: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
        <div className="container-xl py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "60+", label: "Exhibitor Booths" },
              { value: "20+", label: "Startup Pods" },
              { value: "3", label: "Demo Stages" },
              { value: "2", label: "Days of Access" },
            ].map((stat) => (
              <div key={stat.label} className="text-center" data-testid={`expo-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="text-4xl font-black text-white mb-1" style={{ fontFamily: "var(--font-heading)" }}>{stat.value}</div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booth Options */}
      <section className="section-py" id="booth-types" data-testid="booth-types-section">
        <div className="container-xl">
          <RevealSection>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
              <div>
                <div className="overline-label mb-4">Exhibition Options</div>
                <h2 className="text-4xl font-black text-white" style={{ fontFamily: "var(--font-heading)" }}>
                  Every scale. Every business need.
                </h2>
              </div>
              <button onClick={scrollToForm} className="btn-outline flex-shrink-0">
                Request Information <ArrowRight size={13} />
              </button>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BOOTHS.map((booth, i) => {
              const Icon = booth.icon;
              return (
                <RevealSection key={booth.title} delay={i * 0.1}>
                  <div className="card card-glow p-8 h-full flex flex-col" data-testid={`booth-card-${booth.title.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                      style={{ background: `${booth.color}18`, border: `1px solid ${booth.color}33` }}>
                      <Icon size={20} style={{ color: booth.color }} />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-1" style={{ fontFamily: "var(--font-heading)" }}>{booth.title}</h3>
                    <div className="text-xs mb-5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{booth.size}</div>
                    <ul className="space-y-2 mb-8 flex-1">
                      {booth.perks.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm">
                          <Zap size={12} className="flex-shrink-0 mt-0.5" style={{ color: "var(--cyan)" }} />
                          <span style={{ color: "var(--text-muted)" }}>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={scrollToForm}
                      className="btn-outline w-full justify-center"
                      data-testid={`booth-inquire-btn-${booth.inquiryType}`}
                    >
                      Request Information
                    </button>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Exhibitor Categories */}
      <section className="section-py" id="categories" style={{ backgroundColor: "var(--surface)" }} data-testid="categories-section">
        <div className="container-xl">
          <RevealSection>
            <div className="text-center mb-12">
              <div className="overline-label mb-4">Categories</div>
              <h2 className="text-3xl font-black text-white" style={{ fontFamily: "var(--font-heading)" }}>
                Find the solutions you need.
              </h2>
            </div>
          </RevealSection>
          <RevealSection delay={0.1}>
            <div className="flex flex-wrap gap-3 justify-center">
              {EXHIBITOR_CATEGORIES.map((cat) => (
                <span key={cat} className="tag-chip">{cat}</span>
              ))}
            </div>
          </RevealSection>
        </div>
      </section>

      {/* Startup Zone */}
      <section className="section-py" id="startups" data-testid="startup-zone-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <RevealSection>
              <div>
                <div className="overline-label mb-4">Startup & Demo Zone</div>
                <h2 className="text-4xl font-black text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  Where emerging AI meets early adopters.
                </h2>
                <p className="text-base leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
                  The Core AI Startup Zone is an application-based showcase for emerging AI companies. Connect with customers, gather feedback, and get in front of investors.
                </p>
                <div className="space-y-3 mb-8">
                  {["Dedicated pitch sessions on the Startup Stage", "Direct access to enterprise buyers", "Investor networking event access", "Featured in Canada's top AI startup directory"].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "var(--cyan)" }} />
                      <span className="text-sm" style={{ color: "var(--text-muted)" }}>{item}</span>
                    </div>
                  ))}
                </div>
                <button onClick={scrollToForm} className="btn-primary">
                  Apply for Startup Zone <ArrowRight size={14} />
                </button>
              </div>
            </RevealSection>
            <RevealSection delay={0.15}>
              <img src={IMAGES.workshop} alt="Startup demos" className="rounded-2xl w-full object-cover h-72 opacity-85" />
            </RevealSection>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section ref={formRef} id="inquire" className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="exhibitor-inquiry-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <RevealSection>
              <div>
                <div className="overline-label mb-4">Exhibit at Core AI Summit</div>
                <h2 className="text-4xl font-black text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  Tell us about your company.
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: "var(--text-muted)" }}>
                  Share your details and our expo team will follow up with full availability, booth options, and everything you need to get started.
                </p>
                <div className="space-y-4">
                  {[
                    { label: "No commitment required", desc: "We'll answer your questions before you decide" },
                    { label: "Limited spots available", desc: "Premium and startup positions fill early each cycle" },
                    { label: "Founding exhibitor advantage", desc: "Early exhibitors get priority positioning" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "var(--cyan)" }} />
                      <div>
                        <div className="text-sm font-semibold text-white">{item.label}</div>
                        <div className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>
            <RevealSection delay={0.15}>
              <div className="p-8 rounded-2xl" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                <h3 className="text-xl font-black text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  Exhibitor Inquiry
                </h3>
                <ExhibitorInquiryForm />
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      <CTABand
        title="Attend as a Visitor"
        subtitle="Not ready to exhibit? Join 1,500+ AI leaders as an attendee."
        primaryLabel="Join Priority List"
        secondaryLabel="Sponsor the Event"
        secondaryHref="/partner"
      />
    </div>
  );
}
